import React from 'react';
import escolarAiMark from '../../assets/escolarai-cursor.svg';

interface LoadingProps {
  message?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

// Escala do brand loader — tamanho da logo e da mensagem.
const SIZE_MAP: Record<
  NonNullable<LoadingProps['size']>,
  { logo: string; label: string; gap: string }
> = {
  sm: { logo: 'size-10', label: 'text-xs',  gap: 'gap-2' },
  md: { logo: 'size-14', label: 'text-sm',  gap: 'gap-3' },
  lg: { logo: 'size-20', label: 'text-base', gap: 'gap-4' },
};

/**
 * BrandLoader — logo EscolarAI com pulso suave + mensagem.
 *
 * Sem anel/spinner: a "acao" de carregamento e comunicada pela logo com
 * animate-pulse (opacidade indo e voltando devagar). Simples, alinhado
 * com o branding, e evita o efeito "wobble" que os aneis anteriores
 * geraram.
 */
function BrandLoader({
  size = 'md',
  message,
}: {
  size: NonNullable<LoadingProps['size']>;
  message?: string;
}) {
  const s = SIZE_MAP[size];
  return (
    <div className={`flex flex-col items-center ${s.gap}`}>
      <img
        src={escolarAiMark}
        alt="EscolarAI"
        className={`${s.logo} object-contain animate-pulse`}
        style={{ animationDuration: '1.6s' }}
        draggable={false}
      />
      {message && (
        <p className={`${s.label} font-medium text-gray-600`}>{message}</p>
      )}
    </div>
  );
}

/**
 * Loading — indicador de carregamento padrão do app.
 *
 * Logo EscolarAI com pulso suave + mensagem opcional. Usado por
 * PageLoading, ComponentLoading e diretamente pelos consumidores legados
 * que ainda importam Loading.
 */
export const Loading: React.FC<LoadingProps> = ({
  message = 'Carregando...',
  size = 'md',
  className = '',
}) => {
  return (
    <div
      className={`flex items-center justify-center ${className}`}
      role="status"
      aria-live="polite"
      aria-label={message || 'Carregando'}
    >
      <BrandLoader size={size} message={message} />
    </div>
  );
};

// Loading para páginas inteiras — centralizado com min-height padrão.
export const PageLoading: React.FC<{ message?: string }> = ({ message }) => (
  <div className="flex min-h-[60vh] w-full items-center justify-center p-8">
    <Loading message={message} size="lg" />
  </div>
);

// Loading para componentes — centralizado com padding padrão.
export const ComponentLoading: React.FC<{ message?: string }> = ({ message }) => (
  <div className="flex w-full items-center justify-center p-8">
    <Loading message={message} size="md" />
  </div>
);

// Loading para tabelas
export function TableLoading({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex items-center space-x-4 p-4 bg-white rounded-lg border border-interface">
          <div className="h-10 w-10 bg-gray-200 rounded-full animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: `${Math.random() * 40 + 60}%` }} />
            <div className="h-3 bg-gray-200 rounded animate-pulse" style={{ width: `${Math.random() * 30 + 40}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// Loading para cards
export function CardLoading({ cards = 4 }: { cards?: number }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: cards }).map((_, index) => (
        <div key={index} className="p-6 bg-white rounded-lg border border-interface">
          <div className="flex items-center justify-between mb-4">
            <div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: '60%' }} />
            <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
          </div>
          <div className="space-y-2">
            <div className="h-6 bg-gray-200 rounded animate-pulse" style={{ width: '40%' }} />
            <div className="h-3 bg-gray-200 rounded animate-pulse" style={{ width: '80%' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// Loading para formulários
export function FormLoading() {
  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: '30%' }} />
        <div className="h-10 bg-gray-200 rounded animate-pulse" />
      </div>
      <div className="space-y-4">
        <div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: '40%' }} />
        <div className="h-10 bg-gray-200 rounded animate-pulse" />
      </div>
      <div className="space-y-4">
        <div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: '35%' }} />
        <div className="h-20 bg-gray-200 rounded animate-pulse" />
      </div>
      <div className="flex space-x-3">
        <div className="h-10 bg-gray-200 rounded animate-pulse" style={{ width: '100px' }} />
        <div className="h-10 bg-gray-200 rounded animate-pulse" style={{ width: '100px' }} />
      </div>
    </div>
  );
}

// Loading para botões
export function ButtonLoading({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  };

  return (
    <div className={`animate-spin rounded-full border-2 border-gray-300 border-t-primary ${sizeClasses[size]}`} />
  );
}

// Loading spinner alias for backward compatibility
export const LoadingSpinner = Loading; 